import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../../common/commonSlice";
import {
  ConsultationFilteredResult,
  ConsultationResultDetail,
  FilterConsultationsReq,
  getConsultationDetailAPI,
  getConsultationDetailByExecFlowIdAPI,
  getFilteredConsultationsAPI,
  retryConsultationAPI,
  startConsultationAPI,
  StartConsultationReq,
} from "./externalInfoAPI";
import { RootState } from "../../../app/store";

export interface ListasState {
  loadingList: LoadingStatus;
  loadingDetail: LoadingStatus;
  loadingStartConsultation: LoadingStatus;
  currentFilteredRequest: FilterConsultationsReq;
  currentConsultation?: ConsultationResultDetail;
  currentConsultationsResult?: ConsultationFilteredResult;
  currentConsultationsExecFlow?: ConsultationResultDetail[];
}

const initialState: ListasState = {
  loadingList: "idle",
  loadingDetail: "idle",
  loadingStartConsultation: "idle",
  currentFilteredRequest: {
    page: 0,
  },
};

export const getFilteredconsultations = createAsyncThunk(
  "manualLists/getFilteredconsultations",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const req = state.manualLists.currentFilteredRequest;
      const result = await getFilteredConsultationsAPI(req);
      if (result.data) {
        return result.data;
      } else {
        return rejectWithValue(result.error);
      }
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const getConsultationDetail = createAsyncThunk(
  "manualLists/getConsultationDetail",
  async (id: string, { rejectWithValue }) => {
    try {
      const result = await getConsultationDetailAPI(id);
      if (result.data) {
        return result.data;
      } else {
        return rejectWithValue(result.error);
      }
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const getConsultationDetailByExecFlowId = createAsyncThunk(
  "manualLists/getConsultationDetailByExecFlowId",
  async (id: string, { rejectWithValue }) => {
    try {
      const result = await getConsultationDetailByExecFlowIdAPI(id);
      if (result.data) {
        return result.data;
      } else {
        return rejectWithValue(result.error);
      }
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const retryConsultation = createAsyncThunk(
  "manualLists/retryConsultation",
  async (id: string, { rejectWithValue }) => {
    try {
      const result = await retryConsultationAPI(id);
      if (result.data) {
        return result.data;
      } else {
        return rejectWithValue(result.error);
      }
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const startConsultation = createAsyncThunk(
  "manualLists/startConsultation",
  async (req: StartConsultationReq, { rejectWithValue }) => {
    try {
      const result = await startConsultationAPI(req);
      if (result.data) {
        return result.data;
      } else {
        return rejectWithValue(result.error);
      }
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const manualListsSlice = createSlice({
  name: "manualLists",
  initialState,
  reducers: {
    changeCurrentFilteredRequest: (
      state,
      action: PayloadAction<FilterConsultationsReq>
    ) => {
      state.currentFilteredRequest = action.payload;
    },
    setCurrentConsultationDetail: (
      state,
      action: PayloadAction<ConsultationResultDetail>
    ) => {
      state.currentConsultation = action.payload;
    },
    setCurrentConsultationsResult: (
      state,
      action: PayloadAction<ConsultationFilteredResult>
    ) => {
      state.currentConsultationsResult = action.payload;
    },
    setCurrentConsultationsExecFlow: (
      state,
      action: PayloadAction<ConsultationResultDetail[] | undefined>
    ) => {
      state.currentConsultationsExecFlow = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get list
    builder
      .addCase(getFilteredconsultations.pending, (state) => {
        state.loadingList = "pending";
      })
      .addCase(getFilteredconsultations.fulfilled, (state, action) => {
        state.loadingList = "resolved";
        state.currentConsultationsResult = action.payload;
      })
      .addCase(getFilteredconsultations.rejected, (state) => {
        state.loadingList = "rejected";
      });

    // Get detail
    builder
      .addCase(getConsultationDetail.pending, (state) => {
        state.loadingDetail = "pending";
      })
      .addCase(getConsultationDetail.fulfilled, (state, action) => {
        state.loadingDetail = "resolved";
        state.currentConsultation = action.payload;
      })
      .addCase(getConsultationDetail.rejected, (state) => {
        state.loadingDetail = "rejected";
      });

    builder
      .addCase(getConsultationDetailByExecFlowId.pending, (state) => {
        state.loadingDetail = "pending";
      })
      .addCase(getConsultationDetailByExecFlowId.fulfilled, (state, action) => {
        state.loadingDetail = "resolved";
        state.currentConsultationsExecFlow = action.payload;
      })
      .addCase(getConsultationDetailByExecFlowId.rejected, (state) => {
        state.loadingDetail = "rejected";
      });

    // retry
    builder
      .addCase(retryConsultation.pending, (state) => {
        state.loadingDetail = "pending";
      })
      .addCase(retryConsultation.fulfilled, (state, action) => {
        state.loadingDetail = "resolved";
        state.currentConsultation = action.payload;
      })
      .addCase(retryConsultation.rejected, (state) => {
        state.loadingDetail = "rejected";
      });

    // start
    builder
      .addCase(startConsultation.pending, (state) => {
        state.loadingStartConsultation = "pending";
      })
      .addCase(startConsultation.fulfilled, (state, action) => {
        state.loadingStartConsultation = "resolved";
        state.currentConsultation = action.payload;
      })
      .addCase(startConsultation.rejected, (state) => {
        state.loadingStartConsultation = "rejected";
      });
  },
});

export const {
  changeCurrentFilteredRequest,
  setCurrentConsultationDetail,
  setCurrentConsultationsResult,
  setCurrentConsultationsExecFlow,
} = manualListsSlice.actions;

export const selectCurrentLoadingConsults = (state: RootState) =>
  state.manualLists.loadingList;
export const selectCurrentLoadingConsultDetail = (state: RootState) =>
  state.manualLists.loadingDetail;
export const selectCurrentLoadingStartConsultation = (state: RootState) =>
  state.manualLists.loadingStartConsultation;
export const selectCurrentFilterdConsultRequest = (state: RootState) =>
  state.manualLists.currentFilteredRequest;
export const selectCurrentConsultation = (state: RootState) =>
  state.manualLists.currentConsultation;
export const selectCurrentConsultationsResult = (state: RootState) =>
  state.manualLists.currentConsultationsResult;
export const selectCurrentConsultationsExecFlow = (state: RootState) =>
  state.manualLists.currentConsultationsExecFlow;

export default manualListsSlice.reducer;
